import { Button, FlexCenter } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useCallback } from "react";
import { APP_CONTENT_WIDTH } from "src/constants/layout";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import useContentForProgram, {
  DynamicContentItemProps
} from "src/lib/useContentForProgram";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import { SubscriptionInterval } from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";
import { toast, useBloc } from "src/state/state";
import Price from "src/ui/components/Price/Price";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import ErrorBox from "src/ui/components/StyledComponents/ErrorBox";
import Translate from "src/ui/components/Translate/Translate";

const EnrollButton: FC<{
  checkMetadataPartnerFunnel?: string;
  onEnroll: () => void;
  onError: () => void;
  subscriptionId: string;
}> = ({ onEnroll, onError, subscriptionId }) => {
  const [, { buySubscription }] = useBloc(SubscriptionCubit);
  const [, { nextStep }] = useBlocNext(SignupCustomBloc);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  return (
    <Button
      theme="charcoal"
      center
      onPress={() => {
        if (isSubmitting) {
          return;
        }

        setIsSubmitting(true);
        onEnroll();
        void buySubscription(subscriptionId, {
          onSuccess: () => void nextStep(),
          onError: (e) => {
            setIsSubmitting(false);
            onError();
            reportErrorSentry(new Error("Error buying subscription"), {
              error: e
            });
            toast.error("error_generic");
          }
        });
      }}
    >
      <Translate msg="enroll_now" />
    </Button>
  );
};

const GenericCheckoutCustomizedCheckoutScreen: FC<{
  checkMetadataPartnerFunnel: string;
  totalPrice: number;
  eligibleFoeMedicalBilling: boolean;
  subscriptionId?: string;
}> = ({
  checkMetadataPartnerFunnel,
  totalPrice,
  eligibleFoeMedicalBilling,
  subscriptionId
}) => {
  const content = useContentForProgram();
  const [, { showSaving, hideSaving }] = useBlocNext(SignupCustomBloc);

  const handleEnroll = useCallback(() => {
    showSaving({
      durationMs: 20000,
      text: translate("savingIndicator.info", { context: "enrolling" })
    });
  }, [showSaving]);

  const handleError = useCallback(() => {
    hideSaving();
  }, [hideSaving]);

  if (!subscriptionId) {
    reportErrorSentry(
      new Error("User does not have funnel specific subscription")
    );

    return (
      <ErrorBox data-severity="error">
        <Translate msg="error.generic" />
      </ErrorBox>
    );
  }

  const dynamicContentProps: DynamicContentItemProps = {
    eligibleFoeMedicalBilling
  };

  return (
    <>
      <nine-info-section
        style={{
          margin: "0.8em 0",
          width: `min(${APP_CONTENT_WIDTH}px, 100vw)`
        }}
      >
        <nine-info-container slot="content">
          <nine-spacer s="md"></nine-spacer>
          {content.image && <content.image {...dynamicContentProps} />}
          <nine-spacer s="md"></nine-spacer>
          <h5 className="strong m0">
            {content.title && <content.title {...dynamicContentProps} />}
          </h5>
          <p className="color-c-80">
            {content.subtitle && <content.subtitle {...dynamicContentProps} />}
          </p>
          <nine-spacer s="xs"></nine-spacer>
          {content.highlights && (
            <content.highlights {...dynamicContentProps} />
          )}

          {content.list && <content.list {...dynamicContentProps} />}
          <nine-spacer s="lg"></nine-spacer>
          <div className="as-little">
            {content.note && <content.note {...dynamicContentProps} />}
          </div>

          <nine-spacer s="md"></nine-spacer>
        </nine-info-container>
      </nine-info-section>

      {totalPrice === 0 && (
        <>
          <nine-info-section>
            <p slot="title" className="m0 strong as-tiny color-c-60">
              <Translate msg="summary" uppercase={true} />
            </p>
            <nine-info-container slot="content">
              <nine-info-row variant="medication" imageSrc={content.imageSrc}>
                {content.image && <content.image {...dynamicContentProps} />}
                <h5 slot="title" className="m0 strong">
                  {content.title && <content.title {...dynamicContentProps} />}
                </h5>

                <p slot="description" className="m0 color-c-80">
                  <Translate msg="membership.checkout.subtitle" />
                </p>
              </nine-info-row>
              <nine-row px0 pyLarge borderGrayLight>
                <p slot="title" className="m0 as-little color-c">
                  <Translate msg="signup_funnel_checkout_item_title_monthly_membership" />{" "}
                  (
                  <Translate msg="covered" />)
                </p>
                <p slot="label" className="m0 as-little color-c text-right">
                  <Price
                    amount={0}
                    duration={SubscriptionInterval.monthly}
                    formatLocale
                    keepNumber
                  />
                </p>
              </nine-row>
              <nine-row px0 pyLarge borderGrayLight>
                <h5 slot="title" className="m0 strong color-c">
                  <Translate msg="total" />
                </h5>
                <h5 slot="label" className="m0 strong color-c text-right">
                  <Price
                    amount={0}
                    formatLocale
                    keepNumber
                    duration={SubscriptionInterval.monthly}
                  />
                </h5>
              </nine-row>
            </nine-info-container>
          </nine-info-section>

          <FlexCenter>
            <EnrollButton
              onEnroll={handleEnroll}
              onError={handleError}
              checkMetadataPartnerFunnel={checkMetadataPartnerFunnel}
              subscriptionId={subscriptionId}
            />
          </FlexCenter>
        </>
      )}
    </>
  );
};

export default GenericCheckoutCustomizedCheckoutScreen;
